import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const formatWon = (n) => `₩${Number(n || 0).toLocaleString('ko-KR')}`;
const formatDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const STATUS_LABEL = {
  trial:     { label: '무료 체험', color: 'var(--info)',    bg: 'var(--info-light)' },
  active:    { label: '구독 중',   color: 'var(--success)', bg: 'var(--success-light)' },
  past_due:  { label: '미납',      color: 'var(--warning)', bg: 'var(--warning-light)' },
  cancelled: { label: '해지됨',    color: 'var(--danger)',  bg: 'var(--danger-light)' },
  expired:   { label: '만료',      color: 'var(--text-tertiary)', bg: '#F2F4F6' },
};

const INVOICE_STATUS = {
  pending:   { label: '결제 대기', color: 'var(--warning)', bg: 'var(--warning-light)' },
  paid:      { label: '결제 완료', color: 'var(--success)', bg: 'var(--success-light)' },
  failed:    { label: '결제 실패', color: 'var(--danger)',  bg: 'var(--danger-light)' },
  cancelled: { label: '취소됨',    color: 'var(--text-tertiary)', bg: '#F2F4F6' },
};

const SectionCard = ({ title, icon, children, action }) => (
  <div style={{
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    marginBottom: 20,
    boxShadow: 'var(--shadow-card)',
    overflow: 'hidden',
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '18px 24px',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
      </div>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const ProgressBar = ({ value, max, label }) => {
  const unlimited = !max || max === 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((value / max) * 100));
  const danger = pct >= 90;
  const warn = pct >= 75;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 6,
        fontSize: '0.85rem',
      }}>
        <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontWeight: 700, color: 'var(--text)' }}>
          {value.toLocaleString('ko-KR')}{unlimited ? '' : ` / ${max.toLocaleString('ko-KR')}`}
          {unlimited && <span style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontWeight: 500 }}>무제한</span>}
        </span>
      </div>
      {!unlimited && (
        <div style={{
          height: 8,
          background: 'var(--border)',
          borderRadius: 999,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            background: danger ? 'var(--danger)' : warn ? 'var(--warning)' : 'var(--primary)',
            transition: 'width 0.4s ease',
          }} />
        </div>
      )}
    </div>
  );
};

const BillingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState({ max_tables: 0, max_menus: 0, max_orders_per_month: 0 });
  const [invoices, setInvoices] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, usageRes, invRes] = await Promise.allSettled([
        api.get('/admin/billing/subscription'),
        api.get('/admin/billing/usage'),
        api.get('/admin/billing/invoices'),
      ]);
      if (subRes.status === 'fulfilled') setSubscription(subRes.value.data.subscription);
      if (usageRes.status === 'fulfilled') {
        setUsage(usageRes.value.data.usage);
        setLimits(usageRes.value.data.limits || {});
      }
      if (invRes.status === 'fulfilled') setInvoices(invRes.value.data.invoices || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/billing/cancel');
      await fetchAll();
      setShowCancelConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/billing/reactivate');
      await fetchAll();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <div className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
        </div>
      </div>
    );
  }

  const statusMeta = STATUS_LABEL[subscription?.status] || STATUS_LABEL.active;
  const isTrial = !!subscription?.is_in_trial;
  const willCancel = subscription?.cancel_at_period_end === 1;

  const monthly = subscription?.monthly_price || 0;
  const yearly = subscription?.yearly_price || 0;
  const currentPrice = subscription?.billing_cycle === 'yearly' ? yearly : monthly;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">구독 관리</h1>
          <p className="page-subtitle">플랜, 사용량, 청구서를 관리합니다</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" onClick={() => navigate('/plans')}>
            플랜 변경
          </button>
        </div>
      </div>

      {/* Current plan */}
      <div style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #FBF6E9 0%, #FFFFFF 60%)',
        border: '1px solid var(--primary)',
        borderRadius: 'var(--radius-lg)',
        padding: '28px 32px',
        marginBottom: 20,
        boxShadow: '0 4px 24px rgba(191,155,48,0.08)',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: -40, right: -40,
          width: 160, height: 160,
          background: 'radial-gradient(circle, rgba(191,155,48,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              background: statusMeta.bg,
              color: statusMeta.color,
              borderRadius: 999,
              fontSize: '0.75rem',
              fontWeight: 700,
              marginBottom: 12,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusMeta.color }} />
              {statusMeta.label}
            </div>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: 'var(--text)',
              letterSpacing: '-0.5px',
              marginBottom: 6,
            }}>
              {subscription?.plan_name || '플랜 없음'}
            </h2>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {subscription?.plan_description}
            </p>
          </div>
          <div style={{ textAlign: 'right', minWidth: 180 }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 500 }}>
              {subscription?.billing_cycle === 'yearly' ? '연간 결제' : '월간 결제'}
            </div>
            <div style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--primary)', letterSpacing: '-1px' }}>
              {formatWon(currentPrice)}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              / {subscription?.billing_cycle === 'yearly' ? '년' : '월'}
            </div>
          </div>
        </div>

        <div style={{
          marginTop: 24,
          paddingTop: 20,
          borderTop: '1px solid rgba(191,155,48,0.2)',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 20,
        }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>
              {isTrial ? '체험 종료일' : '다음 결제일'}
            </div>
            <div style={{ fontWeight: 700, color: 'var(--text)' }}>
              {isTrial ? formatDate(subscription?.trial_ends_at) : formatDate(subscription?.next_billing_at)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>시작일</div>
            <div style={{ fontWeight: 700, color: 'var(--text)' }}>{formatDate(subscription?.started_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: 4 }}>플랫폼 수수료</div>
            <div style={{ fontWeight: 700, color: 'var(--text)' }}>
              {subscription ? `${(subscription.platform_fee_rate * 100).toFixed(1)}%` : '-'}
            </div>
          </div>
        </div>

        {willCancel && (
          <div style={{
            marginTop: 20,
            padding: '12px 16px',
            background: 'var(--warning-light)',
            border: '1px solid var(--warning)',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--warning-dark, #E08600)', fontWeight: 600 }}>
              {formatDate(subscription?.current_period_end)}에 해지될 예정입니다.
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleReactivate}
              disabled={actionLoading}
            >
              해지 취소
            </button>
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/plans')}>
            플랜 업그레이드
          </button>
          {!willCancel && subscription?.status !== 'cancelled' && (
            <button
              className="btn btn-outline"
              onClick={() => setShowCancelConfirm(true)}
              style={{ color: 'var(--danger)', borderColor: 'var(--danger-light)' }}
            >
              구독 해지
            </button>
          )}
        </div>
      </div>

      {/* Usage */}
      <SectionCard
        title="이번 달 사용량"
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        }
      >
        <div style={{ padding: '20px 24px' }}>
          <ProgressBar label="주문 수" value={usage?.orders || 0} max={limits.max_orders_per_month || 0} />
          <ProgressBar label="테이블"  value={usage?.tables || 0} max={limits.max_tables || 0} />
          <ProgressBar label="메뉴"    value={usage?.menus || 0}  max={limits.max_menus || 0} />
        </div>
      </SectionCard>

      {/* Payment method (placeholder) */}
      <SectionCard
        title="결제 수단"
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
            <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
          </svg>
        }
        action={
          <button className="btn btn-outline btn-sm" onClick={() => alert('결제 수단 등록 기능은 준비 중입니다')}>
            등록/변경
          </button>
        }
      >
        <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 56, height: 38,
            background: 'linear-gradient(135deg, #BF9B30 0%, #A07D1A 100%)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: 0.5,
          }}>CARD</div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--text)' }}>등록된 결제 수단이 없습니다</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)' }}>
              자동 결제를 위해 결제 수단을 등록해주세요
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Invoices */}
      <SectionCard
        title="청구서"
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        }
      >
        {invoices.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            청구서가 없습니다
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={thStyle}>청구서 번호</th>
                  <th style={thStyle}>발행일</th>
                  <th style={thStyle}>금액</th>
                  <th style={thStyle}>상태</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>액션</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const meta = INVOICE_STATUS[inv.status] || INVOICE_STATUS.pending;
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text)' }}>
                          {inv.invoice_number}
                        </span>
                      </td>
                      <td style={tdStyle}>{formatDate(inv.created_at)}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{formatWon(inv.total)}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: 999,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          background: meta.bg,
                          color: meta.color,
                        }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => navigate(`/invoice/${inv.id}`)}
                        >
                          보기
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Cancel modal */}
      {showCancelConfirm && (
        <div
          onClick={() => setShowCancelConfirm(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              padding: 28,
              maxWidth: 440,
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 10, color: 'var(--text)' }}>
              구독을 해지하시겠습니까?
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
              현재 결제 주기 종료일({formatDate(subscription?.current_period_end)})까지는 서비스를 계속 이용할 수 있습니다.
              그 이후에는 서비스가 중단됩니다.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowCancelConfirm(false)}>
                취소
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={handleCancel}
                disabled={actionLoading}
              >
                {actionLoading ? '처리 중...' : '해지하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '12px 24px',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
const tdStyle = {
  padding: '14px 24px',
  fontSize: '0.88rem',
  color: 'var(--text)',
};

export default BillingPage;
